<script setup lang="ts" generic="Row extends Record<string, unknown>">
import { computed, nextTick, ref, watch, type Ref } from 'vue'
import SourceEmptyState from '~/components/ui/SourceEmptyState.vue'
import SourceFieldHelp from '~/components/ui/SourceFieldHelp.vue'
import SourceTableColumnFilterMenu from '~/components/ui/SourceTableColumnFilterMenu.vue'
import type {
  SourceTableAction,
  SourceTableColumn,
  SourceTableMode,
  SourceTableSeparator,
  SourceTableSortChange,
  SourceTableSortDirection,
  SourceTableStyle,
} from '~/types/sourceTable'

const props = withDefaults(
  defineProps<{
    columns: SourceTableColumn<Row>[]
    rows: Row[]
    actions?: SourceTableAction<Row>[]
    /** Mode édition (formulaires) ou lecture (listes admin). Défaut : editable. */
    mode?: SourceTableMode
    editableKeys?: string[]
    tableStyle?: SourceTableStyle
    separator?: SourceTableSeparator
    emptyTitle?: string
    emptyDescription?: string
    /** Icône Material de l’empty state (défaut : inbox). Corbeille / suppressions : `delete`. */
    emptyIcon?: string
    getRowKey?: (row: Row, rowIndex: number) => string | number
    /** Clé de tri active (contrôlée). */
    sortKey?: string | null
    /** Direction de tri active (contrôlée). */
    sortDirection?: SourceTableSortDirection | null
    /** Filtres de colonnes appliqués (contrôlés) : columnKey → values. */
    columnFilters?: Record<string, string[]>
  }>(),
  {
    actions: () => [],
    mode: 'editable',
    editableKeys: () => [],
    tableStyle: 'classic',
    separator: 'horizontal',
    emptyTitle: 'Aucun résultat',
    emptyDescription: undefined,
    emptyIcon: 'inbox',
    getRowKey: undefined,
    sortKey: null,
    sortDirection: null,
    columnFilters: () => ({}),
  }
)

const emit = defineEmits<{
  (e: 'update:rows', rows: Row[]): void
  (e: 'sort-change', payload: SourceTableSortChange): void
  (e: 'filter-change', payload: { columnKey: string; values: string[] }): void
}>()

const isReadonly = computed(() => props.mode === 'readonly')
const isEmpty = computed(() => props.rows.length === 0)

function columnSortKey(column: SourceTableColumn<Row>): string {
  return column.sortKey ?? column.key
}

function ariaSortForColumn(column: SourceTableColumn<Row>): 'ascending' | 'descending' | 'none' | undefined {
  if (!column.sortable) return undefined
  if (props.sortKey !== columnSortKey(column) || !props.sortDirection) return 'none'
  return props.sortDirection === 'asc' ? 'ascending' : 'descending'
}

function sortIconForColumn(column: SourceTableColumn<Row>): string {
  if (props.sortKey !== columnSortKey(column) || !props.sortDirection) {
    return 'unfold_more'
  }
  return props.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'
}

function isSortActive(column: SourceTableColumn<Row>): boolean {
  return props.sortKey === columnSortKey(column) && Boolean(props.sortDirection)
}

function onSortClick(column: SourceTableColumn<Row>): void {
  if (!column.sortable) return
  const key = columnSortKey(column)
  if (props.sortKey !== key || !props.sortDirection) {
    emit('sort-change', { key, direction: 'asc' })
    return
  }
  if (props.sortDirection === 'asc') {
    emit('sort-change', { key, direction: 'desc' })
    return
  }
  emit('sort-change', { key: null, direction: null })
}

function filterValuesForColumn(columnKey: string): string[] {
  return props.columnFilters[columnKey] ?? []
}

function onFilterUpdate(columnKey: string, values: string[]): void {
  emit('filter-change', { columnKey, values })
}

function isConsultAction(action: SourceTableAction<Row>): boolean {
  return action.consult === true || action.icon === 'visibility'
}

function isDestructiveAction(action: SourceTableAction<Row>): boolean {
  return action.destructive === true || action.icon === 'delete'
}

/** Consultation : clic / focus sur la ligne entière (readonly). */
const consultAction = computed(() => {
  if (!isReadonly.value) return undefined
  return props.actions.find((action) => isConsultAction(action))
})

/** Actions restantes (édition, suppression…) - overlay au survol en readonly. */
const hoverActions = computed(() => {
  if (!isReadonly.value) return []
  return props.actions.filter((action) => !isConsultAction(action))
})

/** Colonne Actions classique (mode édition). */
const hasEditableActionsColumn = computed(
  () => !isReadonly.value && props.actions.length > 0
)

/** Colonne Actions réservée en lecture (icônes au survol, sans recouvrir le contenu). */
const hasReadonlyActionsColumn = computed(
  () => isReadonly.value && hoverActions.value.length > 0
)

const actionsColSpanExtra = computed(() =>
  hasEditableActionsColumn.value || hasReadonlyActionsColumn.value ? 1 : 0
)

const isRowClickable = computed(() => Boolean(consultAction.value))

function isInteractiveTarget(target: EventTarget | null, currentTarget: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  const interactive = target.closest('a, button, input, textarea, select, [role="button"]')
  /* La ligne elle-même a role="button" : ne pas la compter comme enfant interactif. */
  return Boolean(interactive && interactive !== currentTarget)
}

function activateConsult(row: Row, rowIndex: number): void {
  const action = consultAction.value
  if (!action || action.disabled?.(row)) return
  action.onClick(row, rowIndex)
}

function onRowClick(row: Row, rowIndex: number, event: MouseEvent): void {
  if (!consultAction.value) return
  if (isInteractiveTarget(event.target, event.currentTarget)) return
  activateConsult(row, rowIndex)
}

function onRowKeydown(row: Row, rowIndex: number, event: KeyboardEvent): void {
  if (!consultAction.value) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  activateConsult(row, rowIndex)
}

/** Cast: Vue unwraps generic Row in `ref`, which breaks assignability. */
const internalRows = ref([...props.rows]) as Ref<Row[]>
const tableEl = ref<HTMLTableElement | null>(null)
const activeCell = ref<{ rowIndex: number; colKey: string } | null>(null)
const isEmittingRows = ref(false)

const hasAnyEmptyEditableRow = computed(() =>
  internalRows.value.some((row) => isRowEmpty(row))
)

function isRowEmpty(row: Row) {
  const keysToCheck = props.editableKeys.length
    ? props.editableKeys
    : props.columns.map((column) => column.key)
  return keysToCheck.every((key) => !String(row?.[key] ?? '').trim())
}

function isCellEmpty(row: Row, key: string): boolean {
  return !String(row?.[key] ?? '').trim()
}

function getCellDisplayText(row: Row, column: SourceTableColumn<Row>): string {
  const value = String(row[column.key] ?? '').trim()
  if (value) return value
  return column.placeholder ?? 'Ex.'
}

function cellText(row: Row, key: string): string {
  const value = row[key]
  if (value == null) return ''
  return String(value)
}

function isActiveCell(rowIndex: number, colKey: string): boolean {
  return activeCell.value?.rowIndex === rowIndex && activeCell.value?.colKey === colKey
}

function rowKey(row: Row, rowIndex: number): string | number {
  if (props.getRowKey) return props.getRowKey(row, rowIndex)
  const id = row.id
  if (typeof id === 'string' || typeof id === 'number') return id
  return rowIndex
}

function emitRows() {
  isEmittingRows.value = true
  emit(
    'update:rows',
    internalRows.value.map((row) => ({ ...row }))
  )
  void nextTick(() => {
    isEmittingRows.value = false
  })
}

function updateCell(rowIndex: number, colKey: string, value: string) {
  const current = internalRows.value[rowIndex]
  if (!current) return
  internalRows.value[rowIndex] = { ...current, [colKey]: value }
  emitRows()
}

function activateCell(rowIndex: number, colKey: string) {
  if (isReadonly.value) return
  activeCell.value = { rowIndex, colKey }
  void nextTick(() => {
    const selector = `tbody tr:nth-child(${rowIndex + 1}) td[data-col-key="${colKey}"] input`
    const input = tableEl.value?.querySelector(selector) as HTMLInputElement | null
    input?.focus()
    if (input && input.value) {
      input.select()
    }
  })
}

function deactivateCell() {
  activeCell.value = null
}

function onCellBlur(rowIndex: number, colKey: string, event: FocusEvent) {
  const input = event.target as HTMLInputElement
  updateCell(rowIndex, colKey, input.value)
  void nextTick(() => {
    const nextActive = document.activeElement
    const row = tableEl.value?.querySelector(`tbody tr:nth-child(${rowIndex + 1})`)
    if (nextActive && row && row.contains(nextActive) && nextActive instanceof HTMLInputElement) {
      return
    }
    deactivateCell()
  })
}

watch(
  () => props.rows,
  (newRows) => {
    if (isEmittingRows.value) return
    internalRows.value = newRows.map((row) => ({ ...row }))
    deactivateCell()
  },
  { deep: true }
)

function startEdit() {
  const newRow = {} as Row
  props.columns.forEach((column) => {
    ;(newRow as Record<string, unknown>)[column.key] = ''
  })
  internalRows.value.push(newRow)
  emitRows()
  const rowIndex = internalRows.value.length - 1
  const firstKey = (
    props.editableKeys.length
      ? props.editableKeys
      : props.columns.map((column) => column.key)
  )[0]
  if (firstKey) {
    activateCell(rowIndex, firstKey)
  }
}

function deleteRow(row: Row) {
  const index = internalRows.value.indexOf(row)
  if (index === -1) return
  if (activeCell.value?.rowIndex === index) {
    deactivateCell()
  }
  internalRows.value.splice(index, 1)
  emitRows()
}

defineExpose({
  startEdit,
  deleteRow,
  hasAnyEmptyEditableRow,
})
</script>

<template>
  <div
    class="source-table"
    :class="[
      `source-table--${mode}`,
      `source-table--${tableStyle}`,
      `source-table--sep-${separator}`,
    ]"
  >
    <div v-if="$slots.top" class="source-table__top">
      <slot name="top" />
    </div>

    <div class="source-table__scroll">
      <table ref="tableEl" class="source-table__table">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              scope="col"
              :style="col.width ? { width: col.width } : undefined"
              :class="{ 'source-table__th--right': col.align === 'right' }"
              :aria-sort="ariaSortForColumn(col)"
            >
              <div
                class="source-table__th-inner"
                :class="{ 'source-table__th-inner--right': col.align === 'right' }"
              >
                <span class="source-table__th-label">{{ col.label }}</span>
                <SourceFieldHelp
                  v-if="col.help"
                  :title="col.label"
                  :description="col.help"
                />
                <span
                  v-if="col.sortable || col.filterable"
                  class="source-table__th-controls"
                >
                  <button
                    v-if="col.sortable"
                    type="button"
                    class="source-table__th-control"
                    :class="{ 'is-active': isSortActive(col) }"
                    :aria-label="`Trier la colonne ${col.label}`"
                    @click.stop="onSortClick(col)"
                  >
                    <span class="material-symbols-rounded" aria-hidden="true">
                      {{ sortIconForColumn(col) }}
                    </span>
                  </button>
                  <SourceTableColumnFilterMenu
                    v-if="col.filterable && (col.filterOptions?.length ?? 0) > 0"
                    :column-key="col.key"
                    :column-label="col.label"
                    :options="col.filterOptions ?? []"
                    :model-value="filterValuesForColumn(col.key)"
                    @update:model-value="(values) => onFilterUpdate(col.key, values)"
                  />
                </span>
              </div>
            </th>
            <th
              v-if="hasEditableActionsColumn"
              scope="col"
              class="source-table__th--actions"
            >
              Action
            </th>
            <th
              v-else-if="hasReadonlyActionsColumn"
              scope="col"
              class="source-table__th--actions source-table__th--actions-hover"
            >
              <span class="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>

        <tbody v-if="isReadonly && isEmpty">
          <tr>
            <td
              class="source-table__empty-cell"
              :colspan="columns.length + actionsColSpanExtra"
            >
              <SourceEmptyState
                size="small"
                :icon="emptyIcon"
                :title="emptyTitle"
                :description="emptyDescription"
              >
                <template v-if="$slots.emptyActions" #actions>
                  <slot name="emptyActions" />
                </template>
              </SourceEmptyState>
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr
            v-for="(row, index) in isReadonly ? rows : internalRows"
            :key="rowKey(row, index)"
            class="source-table__row"
            :class="{ 'source-table__row--clickable': isRowClickable }"
            :tabindex="isRowClickable ? 0 : undefined"
            :role="isRowClickable ? 'button' : undefined"
            :aria-label="isRowClickable ? consultAction?.title : undefined"
            @click="onRowClick(row, index, $event)"
            @keydown="onRowKeydown(row, index, $event)"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              class="source-table__cell"
              :class="{ 'source-table__td--right': col.align === 'right' }"
              :data-col-key="col.key"
            >
              <template v-if="isReadonly">
                <slot :name="`cell-${col.key}`" :row="row" :row-index="index">
                  {{ cellText(row, col.key) }}
                </slot>
              </template>
              <template v-else>
                <div
                  v-if="!isActiveCell(index, col.key)"
                  class="source-table__preview"
                  :class="{ 'source-table__preview--placeholder': isCellEmpty(row, col.key) }"
                  tabindex="0"
                  role="button"
                  @click="activateCell(index, col.key)"
                  @keydown.enter.prevent="activateCell(index, col.key)"
                >
                  {{ getCellDisplayText(row, col) }}
                </div>
                <input
                  v-else
                  type="text"
                  class="source-table__input source-table__input--active"
                  :value="String(row[col.key] ?? '')"
                  :placeholder="col.placeholder ?? 'Ex.'"
                  @input="updateCell(index, col.key, ($event.target as HTMLInputElement).value)"
                  @blur="onCellBlur(index, col.key, $event)"
                  @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
                >
              </template>
            </td>

            <td
              v-if="hasEditableActionsColumn"
              class="source-table__td--actions"
            >
              <div class="source-table__actions">
                <button
                  v-for="(action, actionIndex) in actions"
                  :key="`${action.title}-${actionIndex}`"
                  type="button"
                  class="source-table__action"
                  :class="{ 'source-table__action--destructive': isDestructiveAction(action) }"
                  :aria-label="action.title"
                  :disabled="action.disabled?.(row) || undefined"
                  @click.stop="action.onClick(row, index)"
                >
                  <span class="material-symbols-rounded" aria-hidden="true">{{ action.icon }}</span>
                </button>
              </div>
            </td>

            <td
              v-else-if="hasReadonlyActionsColumn"
              class="source-table__td--actions source-table__td--actions-hover"
            >
              <div class="source-table__actions source-table__actions--hover">
                <button
                  v-for="(action, actionIndex) in hoverActions"
                  :key="`${action.title}-${actionIndex}`"
                  type="button"
                  class="source-table__action"
                  :class="{ 'source-table__action--destructive': isDestructiveAction(action) }"
                  :aria-label="action.title"
                  :disabled="action.disabled?.(row) || undefined"
                  @click.stop="action.onClick(row, index)"
                >
                  <span class="material-symbols-rounded" aria-hidden="true">{{ action.icon }}</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>

        <tfoot v-if="$slots.footer">
          <tr>
            <td
              class="source-table__footer-cell"
              :colspan="columns.length + actionsColSpanExtra"
            >
              <slot name="footer" />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;
@use "~/assets/scss/partials/components/source-table";

.source-table__footer-cell > :deep(*) {
  width: 100%;
  box-sizing: border-box;
}
</style>
